import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Maximize2, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * BusinessQualityChecklist
 * ------------------------
 * Ten-criterion rubric scored 1-10 per criterion. Widget in the right rail
 * shows only summary stats (total out of 100, count rated, strong / weak); a
 * "View details" button opens a modal with the full rubric and the
 * "Fill blanks with AI" action inside.
 *
 * Data model on the trade doc:
 *   business_quality: { revenue_growth: { score: 1-10, notes: str, source: 'ai' | 'user' }, ... }
 */
const CRITERIA = [
  { key: "revenue_growth", label: "Revenue Growth",
    prompt: "Is revenue growing consistently, rather than through a temporary surge or acquisitions alone?" },
  { key: "operating_income_trend", label: "Operating Income Trend",
    prompt: "Are operating profits stable or improving across the cycle?" },
  { key: "free_cash_flow_trend", label: "Free Cash Flow",
    prompt: "Does the business reliably convert earnings into cash?" },
  { key: "margins", label: "Margins",
    prompt: "Are margins healthy, durable, and preferably stable or expanding?" },
  { key: "customer_retention", label: "Customer / Contract Retention",
    prompt: "Is revenue recurring or supported by loyal customers and durable contracts?" },
  { key: "debt_trend", label: "Debt Trend",
    prompt: "Is leverage manageable, with adequate cash flow to service debt, and is debt stable or declining?" },
  { key: "management_stability", label: "Management Stability",
    prompt: "Does management allocate capital competently and avoid destructive acquisitions, dilution, or erratic strategy changes?" },
  { key: "forward_guidance", label: "Forward Guidance",
    prompt: "Is the outlook credible and reasonably stable, without repeated reductions?" },
  { key: "competitive_risk", label: "Competitive / Disruption Risk",
    prompt: "How defensible is the company against competitors, technological change, substitution, and industry deterioration?" },
  { key: "christensen_score", label: "Christensen Disruption Score",
    prompt: "Probability that a cheaper, simpler, or technologically superior entrant could displace the company over the next decade. (Rate 10 when the risk is very low.)" },
];

// 1-10 tone bands — mirrors the widget's Strong (>=7) / Weak (<=3) buckets.
const toneForScore = (s) => {
  if (s == null) return "text-zinc-500";
  if (s >= 7) return "text-emerald-400";
  if (s <= 3) return "text-rose-400";
  return "text-amber-400";
};
const bgForScore = (s) => {
  if (s == null) return "bg-transparent border-zinc-700";
  if (s >= 7) return "bg-emerald-400 border-emerald-400";
  if (s <= 3) return "bg-rose-400 border-rose-400";
  return "bg-amber-400 border-amber-400";
};

/** 1-10 dot rating. Click a dot to set that score; click the currently-set
 * dot again to clear. */
const DotRating = ({ value, onChange, testidPrefix }) => (
  <div className="inline-flex items-center gap-1" data-testid={`${testidPrefix}-rating`}>
    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
      const active = value != null && n <= value;
      return (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? null : n)}
          data-testid={`${testidPrefix}-dot-${n}`}
          className={`w-2.5 h-2.5 rounded-full transition-colors border ${
            active ? bgForScore(value) : "bg-transparent border-zinc-700 hover:border-zinc-500"
          }`}
          aria-label={`Rate ${n} of 10`}
        />
      );
    })}
    <span className={`ml-2 text-xs font-mono ${toneForScore(value)}`} data-testid={`${testidPrefix}-value`}>
      {value != null ? `${value}/10` : "—"}
    </span>
  </div>
);

export default function BusinessQualityChecklist({ trade, onTradeUpdate }) {
  const [collapsed, setCollapsed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bq, setBq] = useState(trade?.business_quality || {});
  const [rating, setRating] = useState(false);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (!dirtyRef.current) setBq(trade?.business_quality || {});
  }, [trade?.id, trade?.business_quality]);

  const stats = useMemo(() => {
    const scores = CRITERIA.map((c) => bq?.[c.key]?.score).filter((s) => typeof s === "number");
    const rated = scores.length;
    const total = scores.reduce((a, b) => a + b, 0);
    const strong = scores.filter((s) => s >= 7).length;
    const weak = scores.filter((s) => s <= 3).length;
    return { rated, total, strong, weak };
  }, [bq]);

  const persist = async (next) => {
    dirtyRef.current = true;
    setBq(next);
    try {
      const { data } = await api.put(`/trades/${trade.id}`, { business_quality: next });
      onTradeUpdate?.(data);
      dirtyRef.current = false;
    } catch {
      toast.error("Couldn't save — try again");
      dirtyRef.current = false;
    }
  };

  const setScore = (key, score) => persist({ ...bq, [key]: { ...(bq?.[key] || {}), score, source: "user" } });
  const setNotes = (key, notes) => setBq((prev) => ({ ...prev, [key]: { ...(prev?.[key] || {}), notes, source: "user" } }));
  const commitNotes = () => persist({ ...bq });

  const runAutoRate = async () => {
    if (!trade?.id) return;
    setRating(true);
    try {
      const { data } = await api.post(`/trades/${trade.id}/business-quality/auto-rate`);
      onTradeUpdate?.(data.trade);
      setBq(data.trade?.business_quality || {});
      const filled = (data.filled || []).length;
      if (filled === 0) {
        toast.info("All criteria already scored — the AI didn't overwrite anything");
      } else {
        toast.success(`AI rated ${filled} criter${filled === 1 ? "ion" : "ia"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-rate failed — try again in a moment");
    } finally {
      setRating(false);
    }
  };

  // Tone for the aggregate score (out of 100).
  const totalTone =
    stats.rated === 0 ? "text-zinc-500" :
    stats.total >= 70 ? "text-emerald-400" :
    stats.total <= 30 ? "text-rose-400" : "text-amber-400";
  const barColor =
    stats.total >= 70 ? "bg-emerald-500" : stats.total <= 30 ? "bg-rose-500" : "bg-amber-500";

  return (
    <>
      <div
        className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4"
        data-testid="business-quality-card"
      >
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            data-testid="business-quality-collapse-toggle"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200"
          >
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <h3 className="font-heading text-sm uppercase tracking-wider">Business Quality</h3>
            {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={runAutoRate}
              disabled={rating}
              data-testid="business-quality-regenerate"
              title="Fill blank criteria with AI-generated scores. Your manual ratings are never overwritten."
              className="p-1 text-zinc-500 hover:text-blue-400 disabled:opacity-30"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${rating ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => setModalOpen(true)}
              data-testid="business-quality-expand"
              title="Open the full checklist"
              className="p-1 text-zinc-500 hover:text-blue-400"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {!collapsed && (
        <>
        {/* Summary strip */}
        <div className="mt-3 grid grid-cols-4 gap-2 text-center" data-testid="business-quality-summary">
          <SummaryCell label="Score" value={stats.rated > 0 ? `${stats.total}` : "—"} suffix={stats.rated > 0 ? "/100" : ""} tone={totalTone} testid="bq-total" />
          <SummaryCell label="Rated" value={`${stats.rated}`} suffix="/10" tone="text-zinc-200" testid="bq-rated" />
          <SummaryCell label="Strong" value={stats.strong} tone="text-emerald-400" testid="bq-strong" />
          <SummaryCell label="Weak" value={stats.weak} tone="text-rose-400" testid="bq-weak" />
        </div>

        {stats.rated > 0 && (
          <div className="mt-3 h-1.5 rounded-sm bg-zinc-900 overflow-hidden" data-testid="bq-progress">
            <div className={`h-full ${barColor}`} style={{ width: `${stats.total}%` }} />
          </div>
        )}
        </>
        )}
      </div>

      {/* Full detail modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="bg-[#0a0a0a] border-zinc-900 max-w-3xl max-h-[90vh] p-0 gap-0 flex flex-col"
          data-testid="business-quality-modal"
        >
          <div className="px-6 pt-5 pb-3 border-b border-zinc-900 shrink-0 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="font-heading text-lg font-semibold inline-flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" /> Business Quality · {trade?.ticker || ""}
              </DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">
                Ten-criterion rubric. Each rated 1–10; total shown out of 100. AI-rated items keep your manual edits.
              </p>
            </div>
            <div className={`text-2xl font-mono ${totalTone} shrink-0`} data-testid="bq-modal-total">
              {stats.rated > 0 ? stats.total : "—"}<span className="text-zinc-600 text-sm">/100</span>
            </div>
          </div>

          <div className="px-6 py-4 border-b border-zinc-900 shrink-0 flex items-center justify-between gap-3">
            <div className="flex gap-2 text-[11px] font-mono">
              <span className="px-2 py-0.5 rounded-sm border border-zinc-800 bg-zinc-950 text-zinc-300" data-testid="bq-modal-rated-badge">
                {stats.rated}/10 rated
              </span>
              <span className="px-2 py-0.5 rounded-sm border border-emerald-500/30 bg-emerald-500/5 text-emerald-400">
                {stats.strong} strong
              </span>
              <span className="px-2 py-0.5 rounded-sm border border-rose-500/30 bg-rose-500/5 text-rose-400">
                {stats.weak} weak
              </span>
            </div>
            <Button
              onClick={runAutoRate}
              disabled={rating}
              data-testid="business-quality-auto-rate-btn"
              variant="outline"
              size="sm"
              className="rounded-sm border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-200 h-8 gap-2"
              title="Fill blank criteria with AI-generated scores. Your manual ratings are never overwritten."
            >
              <RefreshCw className={`w-3.5 h-3.5 ${rating ? "animate-spin" : "text-blue-400"}`} />
              {rating ? "Regenerating…" : "Regenerate"}
            </Button>
          </div>

          <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4" data-testid="business-quality-details">
            {CRITERIA.map((c, i) => {
              const row = bq?.[c.key] || {};
              return (
                <div key={c.key} className="border-t border-zinc-900 pt-4 first:border-t-0 first:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-mono text-zinc-500">{String(i + 1).padStart(2, "0")}</div>
                      <div className="text-sm font-medium text-zinc-100 mt-0.5 inline-flex items-center gap-2">
                        {c.label}
                        {row.source === "ai" && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm border border-blue-500/30 bg-blue-500/10 text-blue-300 font-mono"
                            data-testid={`bq-${c.key}-ai-badge`}
                            title="Generated by AI — edit to override"
                          >
                            <Sparkles className="w-2.5 h-2.5" /> AI
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{c.prompt}</p>
                    </div>
                    <div className={`text-xl font-mono ${toneForScore(row.score)} shrink-0`}>
                      {row.score != null ? `${row.score}` : "—"}
                      <span className="text-zinc-600 text-xs">/10</span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <DotRating
                      value={row.score ?? null}
                      onChange={(v) => setScore(c.key, v)}
                      testidPrefix={`bq-${c.key}`}
                    />
                  </div>
                  <Textarea
                    value={row.notes || ""}
                    onChange={(e) => setNotes(c.key, e.target.value)}
                    onBlur={commitNotes}
                    placeholder="Notes (optional)…"
                    data-testid={`bq-${c.key}-notes`}
                    className="mt-3 min-h-[52px] bg-zinc-950 border-zinc-800 rounded-sm text-xs text-zinc-200 font-mono"
                  />
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const SummaryCell = ({ label, value, suffix, tone, testid }) => (
  <div className="border border-zinc-900 bg-[#0d0d0d] rounded-sm py-2" data-testid={testid}>
    <div className={`text-lg font-mono ${tone}`}>
      {value}
      {suffix ? <span className="text-zinc-600 text-[10px]">{suffix}</span> : null}
    </div>
    <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
  </div>
);
